import { Injectable } from '@angular/core';
import * as firebase from 'firebase';
import { AngularFirestore } from '@angular/fire/firestore';
import { from, Observable, throwError, Subscriber, BehaviorSubject } from "rxjs";
import { catchError, take, exhaustMap } from 'rxjs/operators';
import { FirebaseErrorMessage } from '../enums/firebase-error-message.enum';
import { FirebaseError } from '../enums/firebase-error.enum';
import { Practitioner } from '../models/practitioner.model';
import { PractitionerService } from './practitioner.service';
import { User } from '../models/user.model';

@Injectable()
export class AuthService {
  user = new BehaviorSubject<User>(null);

  constructor(private firestore: AngularFirestore, private practitionerService: PractitionerService){}

  isAuthenticated(): Observable<firebase.User> {
    return new Observable((subscriber: Subscriber<firebase.User>) => {
      let unsubscribe = firebase.auth().onAuthStateChanged((user: firebase.User) => {
        subscriber.next(user);
        subscriber.complete();
        unsubscribe();
      });
    });
  }

  autoLogin():void {
    firebase.auth().onAuthStateChanged((user: firebase.User) => {
      if (user) {
        let zayaUser = new User(user.email, user.uid);
        if (zayaUser.isAdmin) {
          this._setUser(zayaUser);
        } else {
          this.practitionerService.getPractitioner(user.uid).subscribe((practitioner: Practitioner) => {
            this._setUser(practitioner);
          }, _ => {
            this.logout();
          });
        }
      } else {
        this._setUser(null);
      }
    });
  }

  register(practitioner: Practitioner, password: string): Promise<firebase.functions.HttpsCallableResult> {
    let registerPractitioner =  firebase.functions().httpsCallable('registerPractitioner');
    return registerPractitioner({ uid: practitioner.id, email: practitioner.email, password: password });
  }

  login(email: string, password: string): Observable<firebase.auth.UserCredential> {
    return from(firebase.auth().signInWithEmailAndPassword(email, password))
      .pipe(catchError(errorResponse => this._handleAuthError(errorResponse)));
  }

  logout(): Promise<void> {
    this._setUser(null);
    return firebase.auth().signOut();
  }

  private _setUser(user: User) {
    this.user.next(user);
  }

  private _handleAuthError(errorResponse: any): Observable<never> {
    if (!errorResponse.code) { return throwError(FirebaseErrorMessage.AuthDefault.valueOf()); }
    switch (errorResponse.code) {
      case FirebaseError.AuthInvalidEmail:
        return throwError(FirebaseErrorMessage.AuthInvalidEmail.valueOf());
      case FirebaseError.AuthUserNotFound:
      case FirebaseError.AuthWrongPassword:
        return throwError(FirebaseErrorMessage.AuthWrongPassword.valueOf());
      case FirebaseError.AuthWeakPassword:
        return throwError(FirebaseErrorMessage.AuthWeakPassword.valueOf());
      case FirebaseError.AuthEmailAlreadyInUse:
        return throwError(FirebaseErrorMessage.AuthEmailAlreadyInUse.valueOf());
      case FirebaseError.AuthOperationNotAllowed:
      case FirebaseError.AuthUserDisabled:
      default:
        return throwError(FirebaseErrorMessage.AuthDefault.valueOf());
    }
  }
}
